import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { Form, FormField } from '@/Core/UI/Form'
import {
    EntityBody,
    EntityHero,
    EntityLayout,
    EntityPanel,
    EntityTabs,
    FieldCell,
    FieldGrid,
    FieldSection,
} from '@/Ship/Components/EntityCard'

import { CrmCommentsAside } from '../Comments/CrmCommentsAside'
import { CrmLettersTab, canReadLetters } from '../Letters'
import { CrmContactCardHeader } from './CrmContactCardHeader'

import type { CrmContactFormContextLike } from './CrmContactCardHeader'
import type { ReactNode } from 'react'

/**
 * Блоки картки контакту (`SPEC-009` §8.5), кожен відповідає на одне питання:
 *
 *  - **Головне** — хто це: ім'я, прізвище, посада, коментар;
 *  - **Контакти** — як зв'язатися: пошта й телефон;
 *  - **Додатково** — рідше потрібне: англомовне ПІБ, дата народження, а також
 *    поля, додані в ресурс пізніше (щоб не зникали мовчки);
 *  - **Службова інформація** — хто й коли створив і змінив, останнім.
 */
const SECTIONS = {
    main: ['name', 'lastName', 'position', 'comment'],
    contacts: ['email', 'phone'],
    extra: ['fullNameEnglish', 'birthDay'],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину: довгий текст. */
const WIDE = new Set<string>(['comment'])

const PLACED = new Set<string>(Object.values(SECTIONS).flat())

interface CrudContextLike {
    resource?: { fields?: Record<string, unknown> }
}

interface TemplateProps {
    crudContext: CrudContextLike | undefined
    formContext: CrmContactFormContextLike | undefined
}

export interface CrmContactCardOptions {
    /** Колекція коментарів контакту: `/services/client_contact_comments` */
    commentsRestUrl: string
    /** Поле контакту в листі — вкладка «Листи» (`SPEC-013` §9.2) */
    lettersOwner: 'clientContact' | 'carrierContact'
}

/**
 * Картка контакту клієнта чи перевізника на спільних правилах карток
 * (`SPEC-008` §7.1, `SPEC-009` §8.5): шапка з назвою й контактами, блоки
 * полів двома стовпцями ліворуч, стрічка коментарів контакту праворуч.
 *
 * Шаблон один на обох власників — відрізняються лише колекцією коментарів і
 * полем контакту в листі, тож власник отримує його з фабрики.
 *
 * Вкладки — «Загальне» й «Листи» (`SPEC-009` §8.5, `SPEC-010` §9.6;
 * легасі `ClientContactFormTemplate.jsx:31-41`): «Листи» — лише в збереженого
 * контакту й тому, хто бачить листи. Без неї вкладок немає зовсім — смуга з
 * однією вкладкою нічого не дає.
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid` (`SPEC-002`
 * §4.2): шапка й стрічка — окремі observer-компоненти.
 */
export const createCrmContactCardTemplate = ({ commentsRestUrl, lettersOwner }: CrmContactCardOptions) =>
    observer(({ crudContext, formContext }: TemplateProps) => {
        // antd тримає вкладку змонтованою: «Листи» перечитуються при кожному відкритті
        const [lettersEpoch, setLettersEpoch] = useState(0)
        const fields = crudContext?.resource?.fields ?? {}
        const uuid = formContext?.values?.uuid
        const isNew = typeof uuid !== 'string' || uuid === ''

        // Поля, додані в ресурс пізніше, не мають зникати мовчки
        const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

        const cell = (name: string): ReactNode =>
            name in fields ? (
                <FieldCell key={name} wide={WIDE.has(name)}>
                    <FormField name={name} />
                </FieldCell>
            ) : null

        const grid = (names: readonly string[]) => (
            <Form formContext={formContext}>
                <FieldGrid>{names.map(cell)}</FieldGrid>
            </Form>
        )

        const main = (
            <>
                <FieldSection title="Головне">{grid(SECTIONS.main)}</FieldSection>
                <FieldSection title="Контакти">{grid(SECTIONS.contacts)}</FieldSection>
                <FieldSection title="Додатково">{grid([...SECTIONS.extra, ...rest])}</FieldSection>
                <FieldSection title="Службова інформація" hidden={isNew}>
                    {grid(SECTIONS.service)}
                </FieldSection>
            </>
        )

        const body = (
            <EntityBody
                main={main}
                aside={
                    <CrmCommentsAside
                        restUrl={commentsRestUrl}
                        ownerField="contact"
                        formContext={formContext}
                        unsavedText="Коментувати можна буде після збереження контакту."
                    />
                }
            />
        )

        if (!isNew && formContext && canReadLetters()) {
            return (
                <EntityLayout>
                    <EntityTabs
                        header={<CrmContactCardHeader formContext={formContext} />}
                        onChange={(key) => {
                            if (key === 'letters') setLettersEpoch((epoch) => epoch + 1)
                        }}
                        items={[
                            { key: 'general', label: 'Загальне', children: body },
                            {
                                key: 'letters',
                                label: 'Листи',
                                children: (
                                    <EntityPanel>
                                        <CrmLettersTab
                                            key={lettersEpoch}
                                            owner={lettersOwner}
                                            ownerFormContext={formContext}
                                        />
                                    </EntityPanel>
                                ),
                            },
                        ]}
                    />
                </EntityLayout>
            )
        }

        return (
            <EntityLayout>
                {!isNew && formContext ? (
                    <EntityHero>
                        <CrmContactCardHeader formContext={formContext} />
                    </EntityHero>
                ) : null}
                {body}
            </EntityLayout>
        )
    })
